import type { BaseModelAttributes } from './base';
import { I18nLabels } from './common';
import { Locale } from './locale';
import { VipLevel } from './user';

export enum ModelType {
  LLM = 'llm',
  LLM_VL = 'llm_vl',
  TEXT_TO_IMAGE = 'text2image',
  IMAGE_TO_VIDEO = 'image2video',
  TEXT_TO_VIDEO = 'text2video',
  KEYFRAME_TO_VIDEO = 'keyframe2video',
  MULTI_IMAGE_TO_VIDEO = 'multi_image2video',
  IMAGE_EDIT = 'image_edit',
  TEXT_TO_SPEECH = 'text2speech',
  VIDEO_LIPSYNC = 'video_lipsync',
  TEXT_TO_MUSIC = 'text2music',
  SPEECH_TO_TEXT = 'speech2text',
  AUDIO_ANALYSIS = 'audio_analysis',
  MUSIC_EDIT = 'music_edit',
  AUDIO_EDIT = 'audio_edit',
  AVATAR_TRAIN = 'avatar_train',
  AVATAR_VIDEO = 'avatar_video'
}

/**
 * 模型部署区域
 */
export enum ModelRegion {
  /** 国内 */
  CN = 'cn',
  /** 国际/国外 */
  INTL = 'intl',
  /** 全部/通用 */
  ALL = 'all'
}

export const ModelTypeLabels: I18nLabels<ModelType> = {
  [ModelType.LLM]: { [Locale.ZH_CN]: '大语言模型', [Locale.EN_US]: 'LLM' },
  [ModelType.LLM_VL]: { [Locale.ZH_CN]: '大语言模型(VL)', [Locale.EN_US]: 'LLM (VL)' },
  [ModelType.TEXT_TO_IMAGE]: { [Locale.ZH_CN]: '文生图片', [Locale.EN_US]: 'Text to Image' },
  [ModelType.IMAGE_TO_VIDEO]: { [Locale.ZH_CN]: '图生视频', [Locale.EN_US]: 'Image to Video' },
  [ModelType.TEXT_TO_VIDEO]: { [Locale.ZH_CN]: '文生视频', [Locale.EN_US]: 'Text to Video' },
  [ModelType.KEYFRAME_TO_VIDEO]: { [Locale.ZH_CN]: '首尾帧视频', [Locale.EN_US]: 'Keyframe Video' },
  [ModelType.MULTI_IMAGE_TO_VIDEO]: { [Locale.ZH_CN]: '多图生视频', [Locale.EN_US]: 'Multi-Image Video' },
  [ModelType.IMAGE_EDIT]: { [Locale.ZH_CN]: '图片编辑', [Locale.EN_US]: 'Image Edit' },
  [ModelType.TEXT_TO_SPEECH]: { [Locale.ZH_CN]: '文本转语音', [Locale.EN_US]: 'Text to Speech' },
  [ModelType.VIDEO_LIPSYNC]: { [Locale.ZH_CN]: '视频唇形同步', [Locale.EN_US]: 'Video Lip Sync' },
  [ModelType.TEXT_TO_MUSIC]: { [Locale.ZH_CN]: '文生音乐', [Locale.EN_US]: 'Text to Music' },
  [ModelType.SPEECH_TO_TEXT]: { [Locale.ZH_CN]: '语音转文本', [Locale.EN_US]: 'Speech to Text' },
  [ModelType.AUDIO_ANALYSIS]: { [Locale.ZH_CN]: '音频分析', [Locale.EN_US]: 'Audio Analysis' },
  [ModelType.MUSIC_EDIT]: { [Locale.ZH_CN]: '音乐编辑', [Locale.EN_US]: 'Music Edit' },
  [ModelType.AUDIO_EDIT]: { [Locale.ZH_CN]: '音频编辑', [Locale.EN_US]: 'Audio Edit' },
  [ModelType.AVATAR_TRAIN]: { [Locale.ZH_CN]: '数字人训练', [Locale.EN_US]: 'Avatar Training' },
  [ModelType.AVATAR_VIDEO]: { [Locale.ZH_CN]: '数字人视频', [Locale.EN_US]: 'Avatar Video' },
};

export const ModelRegionLabels: I18nLabels<ModelRegion> = {
  [ModelRegion.CN]: { [Locale.ZH_CN]: '国内', [Locale.EN_US]: 'China' },
  [ModelRegion.INTL]: { [Locale.ZH_CN]: '国外', [Locale.EN_US]: 'International' },
  [ModelRegion.ALL]: { [Locale.ZH_CN]: '通用', [Locale.EN_US]: 'All Regions' },
};

export interface ILogicalModel extends BaseModelAttributes {
  title: string;
  description: string;
  modelType: ModelType;
  isEnabled: boolean;
  imageInputCount: number;
  audioInputCount: number;
  videoInputCount: number;
  requiredInputs?: ('image' | 'audio' | 'video')[];
  /** 每秒单价（剧点/秒） */
  coinPrice: number;
  supportedDurations: number[];
  priority: number;
  keyReleaseDelaySeconds: number;
  pollingDelaySeconds: number;
  maxPollingAttempts?: number;
  pollingIntervalSeconds?: number;
  minVipLevel?: VipLevel | null;
  /** 模型部署区域 */
  region?: ModelRegion;
}

/**
 * Complete model record used by legacy/admin code during the route migration.
 * Supplier-specific fields move to IModelRoute and must not be exposed by user APIs.
 */
export interface IModel extends ILogicalModel {
  /** @deprecated Use IModelRoute.provider. */
  provider: string;
  /** @deprecated Use IModelRoute.basePath. */
  basePath: string;
  /** @deprecated Use IModelRoute.modelName. */
  modelName?: string;
  /** @deprecated Use IModelRoute.enableProxy. */
  enableProxy: boolean;
  /** @deprecated Use IModelRoute.vendorId. */
  vendorId?: string;
}

export type IUserModel = Pick<
  ILogicalModel,
  | 'id'
  | 'title'
  | 'description'
  | 'modelType'
  | 'requiredInputs'
  | 'supportedDurations'
  | 'imageInputCount'
  | 'audioInputCount'
  | 'videoInputCount'
  | 'coinPrice'
>;

export interface ModelRouteSummary {
  total: number;
  enabled: number;
  schedulable: number;
  coolingDown: number;
}

export interface IAdminModelSummary extends ILogicalModel {
  routeSummary: ModelRouteSummary;
}

export interface CreateModelRequest {
  title: string;
  description: string;
  modelType: ModelType;
  /** @deprecated Supplier configuration belongs to a model route. */
  provider?: string;
  /** @deprecated Supplier configuration belongs to a model route. */
  basePath?: string;
  /** @deprecated Supplier configuration belongs to a model route. */
  modelName?: string;
  /** @deprecated Supplier configuration belongs to a model route. */
  vendorId?: string;
  isEnabled?: boolean;
  /** @deprecated Supplier configuration belongs to a model route. */
  enableProxy?: boolean;
  imageInputCount?: number;
  audioInputCount?: number;
  videoInputCount?: number;
  requiredInputs?: ('image' | 'audio' | 'video')[];
  coinPrice?: number;
  supportedDurations?: number[];
  priority?: number;
  keyReleaseDelaySeconds?: number;
  pollingDelaySeconds?: number;
  maxPollingAttempts?: number;
  pollingIntervalSeconds?: number;
  minVipLevel?: VipLevel | null;
  region?: ModelRegion;
}

export interface UpdateModelRequest {
  title?: string;
  description?: string;
  provider?: string;
  modelType?: ModelType;
  basePath?: string;
  modelName?: string;
  vendorId?: string;
  isEnabled?: boolean;
  enableProxy?: boolean;
  imageInputCount?: number;
  audioInputCount?: number;
  videoInputCount?: number;
  requiredInputs?: ('image' | 'audio' | 'video')[];
  coinPrice?: number;
  supportedDurations?: number[];
  priority?: number;
  keyReleaseDelaySeconds?: number;
  pollingDelaySeconds?: number;
  maxPollingAttempts?: number;
  pollingIntervalSeconds?: number;
  minVipLevel?: VipLevel | null;
  region?: ModelRegion;
}

export interface BatchUpdateModelRegionRequest {
  ids: string[];
  region: ModelRegion;
}

export interface BatchUpdateModelRegionResponse {
  affectedCount: number;
}

export interface ModelQuery {
  page?: number;
  limit?: number;
  search?: string;
  type?: string;
  modelType?: ModelType;
  provider?: string;
  region?: ModelRegion;
}
